import * as Sentry from "@sentry/react";

let initialized = false;

const BROWSER_EXTENSION_PROTOCOLS = ["chrome-extension://", "moz-extension://"];

function isBrowserExtensionUrl(value: unknown): boolean {
  return (
    typeof value === "string" &&
    BROWSER_EXTENSION_PROTOCOLS.some((protocol) => value.startsWith(protocol))
  );
}

function exceptionIsOnlyBrowserExtensionFrames(
  exception: Sentry.Exception,
): boolean {
  const frames = exception.stacktrace?.frames ?? [];

  return (
    frames.length > 0 &&
    frames.every((frame) => isBrowserExtensionUrl(frame.filename))
  );
}

export function shouldDropBrowserSentryEvent(event: Sentry.Event): boolean {
  // Browser extensions can inject scripts that throw global errors from their
  // own chrome-extension:// or moz-extension:// frames. Those are not
  // ProposalKit failures, so drop events whose exception stack is entirely
  // extension-owned while keeping any event that includes first-party frames.
  return event.exception?.values?.some(exceptionIsOnlyBrowserExtensionFrames) ?? false;
}

export function initSentry(): void {
  if (initialized) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    // Keep parity with the Worker's config — no tracing or PII by default.
    tracesSampleRate: 0,
    sendDefaultPii: false,
    environment: import.meta.env.MODE,
    beforeSend(event) {
      if (shouldDropBrowserSentryEvent(event)) return null;
      return event;
    },
    // Explicit tag so issues can be filtered with `runtime:browser` vs
    // `runtime:worker`. `sdk.name` also disambiguates but a named tag is
    // easier to live with.
    initialScope: { tags: { runtime: "browser" } },
  });
  initialized = true;
}

// Bind the authenticated user's WorkOS id to the Sentry scope so events are
// correlated to a real account. Email intentionally omitted — we set
// `sendDefaultPii: false`, and the id alone is enough to pivot in Sentry.
export function setSentryUser(id: string | null): void {
  if (!initialized) return;
  Sentry.setUser(id ? { id } : null);
}

export { Sentry };
